import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled, { css, keyframes } from "styled-components/macro";

import { faCommentAlt } from "@fortawesome/pro-light-svg-icons";
import { AnimatePresence, motion } from "motion/react";

import { useInterval } from "Utilities/Hooks/useInterval";

import getRandomIcon from "./icons.js";

const loaderEntry = keyframes`
  from { transform: scale(0.7) translateY(80px); }
  to { transform: scale(1) translateY(0); }
`;

const loaderExit = keyframes`
  from { opacity: 1; transform: scale(1) translateY(0); }
  to { opacity: 0; transform: scale(0.7) translateY(80px); }
`;

const iconSlideIn = keyframes`
  from { opacity: 0; transform: translateX(100%) scale(0.7); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;

const iconSlideOut = keyframes`
  from { opacity: 1; transform: translateX(0) scale(1); }
  to { opacity: 0; transform: translateX(-100%) scale(0.7); }
`;

export default function FnrLoadingCarousel({
  show,
  whiteBackground,
  color = "#278dc1",
}) {
  const showRef = useRef(show);
  const [visible, setVisible] = useState(show);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    showRef.current = show;
  }, [show]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      setExiting(false);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show, visible]);

  if (!visible) return null;

  return (
    <Wrapper
      $whiteBackground={whiteBackground}
      $color={color}
      $exiting={exiting}
    >
      <LargeIcon icon={faCommentAlt} />
      <ImageCarousel whiteBackground={whiteBackground} showRef={showRef} />
    </Wrapper>
  );
}

FnrLoadingCarousel.propTypes = {
  show: PropTypes.bool,
  whiteBackground: PropTypes.bool,
};

const Wrapper = styled.div`
  position: absolute;
  z-index: 1000;
  top: 50%;
  left: 50%;
  width: 240px;
  height: 240px;
  margin-left: -120px;
  margin-top: -120px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ $color }) => $color};
  will-change: transform, opacity;
  ${({ $exiting }) =>
    $exiting
      ? css`
          animation: ${loaderExit} 0.3s cubic-bezier(0.6, -0.28, 0.735, 0.045)
            forwards;
        `
      : css`
          animation: ${loaderEntry} 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)
            both;
        `}

  &::after {
    content: "";
    position: absolute;
    bottom: 55px;
    left: 50%;
    transform: translateX(-62%) translateY(-12%);
    width: 0;
    height: 0;
    border-right: 27px solid transparent;
    border-top: 20px solid
      ${({ $whiteBackground }) => ($whiteBackground ? "#fff" : "transparent")};
  }
`;

const LargeIcon = styled(FontAwesomeIcon)`
  font-size: 132px;
  margin-top: 20px;
`;

export function ImageCarousel({ whiteBackground, showRef }) {
  const [icon, setIcon] = useState(getRandomIcon());
  const [exitingIcon, setExitingIcon] = useState(null);

  useInterval(() => {
    if (showRef && !showRef.current) return;
    setExitingIcon(icon);
    setIcon(getRandomIcon());
  }, 500);

  useEffect(() => {
    if (!exitingIcon) return;
    const timer = setTimeout(() => setExitingIcon(null), 300);
    return () => clearTimeout(timer);
  }, [exitingIcon]);

  return (
    <CarouselWrapper $whiteBackground={whiteBackground}>
      {exitingIcon && (
        <CarouselIcon
          key={"exit-" + exitingIcon}
          className={"fa-" + exitingIcon}
          $exiting
        />
      )}
      <CarouselIcon key={"icon-" + icon} className={"fa-" + icon} />
    </CarouselWrapper>
  );
}

ImageCarousel.propTypes = {
  whiteBackground: PropTypes.bool,
  showRef: PropTypes.object,
};

const CarouselWrapper = styled.div`
  height: 89px;
  margin-top: -5px;
  width: 114px;
  margin-left: 0px;
  border-radius: 8px;
  position: absolute;
  overflow: hidden;
  background: ${({ $whiteBackground }) =>
    $whiteBackground ? "#fff" : "transparent"};
  border: ${({ $whiteBackground }) =>
    $whiteBackground ? "1px solid #fff" : "none"};
`;

const CarouselIcon = styled.div`
  font-size: 60px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${({ $exiting }) =>
    $exiting
      ? css`
          animation: ${iconSlideOut} 0.3s ease-out forwards;
        `
      : css`
          animation: ${iconSlideIn} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.4);
        `}
`;

export function FlexibleFnrLoadingCarousel({
  show,
  whiteBackground,
  color = "#278dc1",
}) {
  return (
    <AnimatePresence>
      {show && (
        <FlexibleWrapper
          $whiteBackground={whiteBackground}
          $color={color}
          initial={{ opacity: 1, scale: 0.7, y: 80 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{
            scale: 0.7,
            opacity: 0,
            y: 80,
            transition: {
              type: "tween",
              duration: 0.3,
              ease: "backIn",
            },
          }}
        >
          <LargeIcon icon={faCommentAlt} />
          <ImageCarousel whiteBackground={whiteBackground} />
        </FlexibleWrapper>
      )}
    </AnimatePresence>
  );
}

const FlexibleWrapper = styled(motion.div)`
  position: relative;
  width: 240px;
  height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ $color }) => $color};

  &::after {
    content: "";
    position: absolute;
    bottom: 55px;
    left: 50%;
    transform: translateX(-62%) translateY(-12%);
    width: 0;
    height: 0;
    border-right: 27px solid transparent;
    border-top: 20px solid
      ${({ $whiteBackground }) => ($whiteBackground ? "#fff" : "transparent")};
  }
`;

FlexibleFnrLoadingCarousel.propTypes = {
  show: PropTypes.bool,
  whiteBackground: PropTypes.bool,
  color: PropTypes.string,
};
